import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper,
  Grid,
  IconButton,
  Link,
  Chip,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import { 
  Email, 
  Phone, 
  LinkedIn, 
  GitHub,
  LocationOn 
} from '@mui/icons-material';
import './App.css';

function App() {
  const experiences = [
    {
      company: "Amazon.com",
      role: "Senior Software Engineer",
      location: "Seattle, WA",
      period: "Feb 2017 – Present",
      highlights: [
        "Architected Amazon Pharmacy Auto Ordering platform, leading 20+ engineers for design and development",
        "Led Shipment Consolidation project achieving 37% cost reduction in shipping for Amazon Pharmacy Auto Refills",
        "Led cost reduction program reducing ops cost by 35% and infrastructure cost by 15%",
        "Re-architected Amazon Pharmacy Details Page improving latency by 50% and scaling to handle 10x traffic",
        "Led Multi-Location Fulfillment platform development enabling third-party sellers across India, MENA, and NA",
        "Designed and launched new payment method for India marketplace within 6 months",
        "Mentored and promoted Engineers to SDE2 and SrSDEs",
        "Led operational excellence efforts reducing ticket scores from 90+ to below 60",
        "Wrote tech strategy and vision documents for teams"
      ]
    },
    {
      company: "Flipkart.com",
      role: "SDE 2",
      location: "Bangalore, IN",
      period: "Mar 2014 – Nov 2016",
      highlights: [
        "Tech lead for Customer returns project, reducing fraud by 30%+ within 1 year",
        "Led Data Ingestion component for Crawler team, scraping millions of pages from major ecommerce platforms",
        "Full-stack development for pricing and promotion teams"
      ]
    },
    {
      company: "Ondot",
      role: "Senior Software Engineer",
      location: "Bangalore, IN",
      period: "Oct 2013 – Mar 2014",
      highlights: [
        "Design and development of finance security product"
      ]
    },
    {
      company: "Goldman Sachs",
      role: "Analyst Developer",
      location: "Bangalore, IN",
      period: "Aug 2012 – Jul 2013",
      highlights: [
        "Full stack developer for Office Of Global Security applications"
      ]
    },
    {
      company: "Alcatel-Lucent",
      role: "Senior Software Engineer",
      location: "Bangalore, IN",
      period: "Oct 2010 – Aug 2012",
      highlights: [
        "Developed features for Network Management System (SAM5620)"
      ]
    },
    {
      company: "TCS",
      role: "Assistant System Engineer",
      location: "Bangalore, IN",
      period: "Nov 2009 – Sep 2010",
      highlights: [
        "Software engineer for GE Healthcare projects"
      ]
    }
  ];

  const skills = {
    domain: ["Retail", "HealthCare"],
    technical: ["Java", "Python", "AWS technologies", "Large Scale Systems", "Distributed Systems", "Cloud technologies", "High Availability"],
    leadership: ["Building consensus", "Writing tech strategy/vision", "Cross team collaboration/Leading", "Leading org wide initiatives"],
    others: ["Operational Excellence", "UX", "React", "JS"]
  };

  return (
    <div className="App">
      {/* Navigation */}
      <AppBar position="sticky" elevation={0} className="nav-container">
        <Container maxWidth="lg">
          <Toolbar>
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
              <nav>
                <ul className="nav-links">
                  <li><Link href="#about">About</Link></li>
                  <li><Link href="#experience">Experience</Link></li>
                  <li><Link href="#skills">Skills</Link></li>
                  <li><Link href="#contact">Contact</Link></li>
                </ul>
              </nav>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Header Section */}
      <Box className="header-section">
        <Container maxWidth="lg">
          <Box py={8} textAlign="center" className="fade-in">
            <Typography variant="h2" component="h1" gutterBottom>
              Swayam Biswal
            </Typography>
            <Typography variant="h5" color="textSecondary" gutterBottom>
              Senior Software Engineer
            </Typography>
            <Box display="flex" justifyContent="center" alignItems="center" gap={2} mt={3}>
              <Box display="flex" alignItems="center">
                <LocationOn />
                <Typography>Seattle, WA</Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Email />
                <Link href="mailto:swayammiko@gmail.com" color="inherit" sx={{ ml: 1 }}>
                  swayammiko@gmail.com
                </Link>
              </Box>
              <Box display="flex" alignItems="center">
                <Phone />
                <Typography sx={{ ml: 1 }}>+1-206-351-3354</Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* About Section */}
        <Paper elevation={3} sx={{ p: 4, mb: 4 }} id="about" className="fade-in">
          <Typography variant="h4" gutterBottom>
            About Me
          </Typography>
          <Typography variant="body1" paragraph>
            Senior Engineer with 12+ years of experience in designing, developing scalable software systems to meet business goals. 
            Led high-performing engineering teams and delivered complex projects with high impact. Have worked in fast paced teams 
            and also in mature products with varied experience from building data systems, software platforms & UX improvement projects. 
            Passionate about leveraging cutting-edge technologies to solve challenging problems and drive innovation.
          </Typography>
        </Paper>

        {/* Experience Section */}
        <Paper elevation={3} sx={{ p: 4, mb: 4 }} id="experience" className="fade-in">
          <Typography variant="h4" gutterBottom>
            Experience
          </Typography>
          <Timeline>
            {experiences.map((exp, index) => (
              <TimelineItem key={index}>
                <TimelineSeparator>
                  <TimelineDot />
                  {index < experiences.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <Typography variant="h6" component="h3">
                    {exp.role} at {exp.company}
                  </Typography>
                  <Typography color="textSecondary">
                    {exp.location} | {exp.period}
                  </Typography>
                  <Box mt={1}>
                    <ul style={{ paddingLeft: '20px' }}>
                      {exp.highlights.map((highlight, i) => (
                        <li key={i}>
                          <Typography variant="body2">{highlight}</Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </Paper>

        {/* Skills Section */}
        <Paper elevation={3} sx={{ p: 4, mb: 4 }} id="skills" className="fade-in">
          <Typography variant="h4" gutterBottom>
            Skills
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Domain</Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {skills.domain.map((skill, index) => (
                  <Chip key={index} label={skill} />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Technical</Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {skills.technical.map((skill, index) => (
                  <Chip key={index} label={skill} />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Leadership</Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {skills.leadership.map((skill, index) => (
                  <Chip key={index} label={skill} />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Others</Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {skills.others.map((skill, index) => (
                  <Chip key={index} label={skill} />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Contact Section */}
        <Paper elevation={3} sx={{ p: 4, mb: 4 }} id="contact" className="fade-in">
          <Typography variant="h4" gutterBottom>
            Contact
          </Typography>
          <Box display="flex" justifyContent="center" gap={3}>
            <IconButton href="mailto:swayammiko@gmail.com" color="primary">
              <Email />
            </IconButton>
            <IconButton href="tel:+12063513354" color="primary">
              <Phone />
            </IconButton>
            <IconButton href="https://www.linkedin.com/in/swayambiswal" target="_blank" color="primary">
              <LinkedIn />
            </IconButton>
            <IconButton href="https://github.com/swayamprakash" target="_blank" color="primary">
              <GitHub />
            </IconButton>
          </Box>
        </Paper>
      </Container>
    </div>
  );
}

export default App;